/*
 * TodaysOneThing — Stage-14 Home rebuild.
 *
 * The user brief, verbatim:
 *   "ZONE 2 — Today's One Thing. One primary card only. Choose the highest
 *    value item: report just uploaded → 'Your report is ready'; appointment
 *    soon → 'Prepare for your appointment'; active concern → 'Ask Kindra
 *    about this'; no data → 'Upload your first report'; nothing urgent →
 *    'Continue your last chat'. Must include: why this matters · one CTA ·
 *    optional dismiss."
 *
 * One component. One card. Picked by priority. Calm.
 */
import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, FileText, CalendarBlank, ChatCircleDots,
  Sparkle, UploadSimple,
} from '@phosphor-icons/react';

const Card = ({ eyebrow, headline, sub, ctaLabel, onClick, accent = 'navy', testid }) => {
  const tones = {
    navy:  { bg: '#0E1C2E', fg: '#FBFAF6', eyebrow: 'rgba(251,250,246,0.55)', sub: 'rgba(251,250,246,0.75)', cta: '#FBFAF6', ctaText: '#0E1C2E' },
    cream: { bg: '#FAF7EF', fg: '#0E1C2E', eyebrow: 'rgba(14,28,46,0.45)',     sub: 'rgba(14,28,46,0.65)',     cta: '#0E1C2E', ctaText: '#FBFAF6' },
    teal:  { bg: '#235347', fg: '#FBFAF6', eyebrow: 'rgba(251,250,246,0.55)', sub: 'rgba(251,250,246,0.78)', cta: '#FBFAF6', ctaText: '#235347' },
  };
  const t = tones[accent] || tones.navy;
  return (
    <motion.button
      type="button"
      onClick={onClick}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="w-full text-left rounded-2xl p-6 lg:p-7 hover:opacity-95 transition-opacity"
      style={{ backgroundColor: t.bg, color: t.fg }}
      data-testid={testid || 'todays-one-thing'}
    >
      <p className="text-[10px] tracking-[0.18em] uppercase font-medium mb-2" style={{ color: t.eyebrow }}>
        {eyebrow}
      </p>
      <h2
        className="text-[22px] sm:text-[26px] leading-[1.2] tracking-tight font-normal"
        style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
      >
        {headline}
      </h2>
      <p className="text-[13.5px] mt-2 leading-relaxed max-w-[44ch]" style={{ color: t.sub }}>
        {sub}
      </p>
      <span
        className="inline-flex items-center gap-1.5 mt-4 rounded-full px-3.5 py-1.5 text-[12.5px] font-semibold"
        style={{ backgroundColor: t.cta, color: t.ctaText }}
      >
        {ctaLabel}
        <ArrowRight size={12} weight="bold" />
      </span>
    </motion.button>
  );
};

/**
 * Picks the highest-priority "one thing" based on user state. Order:
 *   1. Report just uploaded (analysed in last 24h) → "Your report is ready"
 *   2. Appointment within 3 days                  → "Prepare for your appointment"
 *   3. No data at all (zero docs, zero chats)    → "Upload your first report"
 *   4. Active chat session in last 24h          → "Continue your last chat"
 *   5. Fallback                                 → "Ask Kindra anything"
 */
export const TodaysOneThing = ({ stats, briefing, recentDocument, nextAppointment, lastChat }) => {
  const navigate = useNavigate();

  // Stage 20B+ Wave B — previously read `stats.documents` / `stats.chats`
  // which don't exist on /home/briefing.stats. The correct field is
  // `documents_count`. `log_count` is used as a proxy for "any chat / note
  // activity" (symptom logs + user notes both flow into it). Without this
  // fix every returning user still saw "Upload your first report" because
  // docs and chats were always 0.
  const docs  = Number(stats?.documents_count || 0);
  const chats = Number(stats?.log_count || 0);

  // 1. Report just uploaded? (`recentDocument` is a document analysed in the
  // last 24h, surfaced via /home/latest-insight or similar.)
  const recentDocAt = recentDocument?.analysed_at || recentDocument?.created_at;
  if (recentDocument && recentDocAt) {
    const ageHours = (Date.now() - new Date(recentDocAt).getTime()) / 3600000;
    if (ageHours <= 24) {
      const label = recentDocument.smart_label || recentDocument.filename || 'Your report';
      return (
        <Card
          accent="teal"
          eyebrow="Today's one thing"
          headline={`Your report is ready.`}
          sub={`Kindra has read ${label} and pulled out what matters. Open it to see the plain-English summary.`}
          ctaLabel="Read the summary"
          onClick={() => navigate(`/documents/${recentDocument.id}`)}
          testid="todays-one-thing-report"
        />
      );
    }
  }

  // 2. Appointment within 3 days?
  if (nextAppointment && nextAppointment.id) {
    const when = nextAppointment.appointment_date || nextAppointment.date;
    if (when) {
      const ageDays = (new Date(when).getTime() - Date.now()) / 86400000;
      if (ageDays >= 0 && ageDays <= 3) {
        return (
          <Card
            accent="navy"
            eyebrow="Today's one thing"
            headline={`Prepare for your appointment.`}
            sub={`Kindra can pull together a one-pager with your symptoms, recent reports, and the questions worth asking.`}
            ctaLabel="Prepare summary"
            onClick={() => navigate(`/appointments/${nextAppointment.id}`)}
            testid="todays-one-thing-appointment"
          />
        );
      }
    }
  }

  // 3. True Day-0 — no data at all → upload first report
  if (docs === 0 && chats === 0 && Number(stats?.conditions_count || 0) === 0) {
    return (
      <Card
        accent="cream"
        eyebrow="Today's one thing"
        headline={<>Upload your first <em className="italic">report.</em></>}
        sub={`Kindra reads it, explains it in plain English, and remembers it for every future answer. Most uploads are ready in under a minute.`}
        ctaLabel="Pick a file"
        onClick={() => navigate('/documents')}
        testid="todays-one-thing-upload"
      />
    );
  }

  // 3b. Stage 20B+ Wave B — user has some profile signal (conditions, logs)
  // but still no documents. Rotate to a report-specific nudge so returning
  // users don't see the blank-slate "Upload your first report" copy forever.
  if (docs === 0 && Number(stats?.conditions_count || 0) > 0) {
    return (
      <Card
        accent="cream"
        eyebrow="Today's one thing"
        headline={<>Add a <em className="italic">report</em> to anchor your record.</>}
        sub={`Your profile is building nicely. Drop in a blood test, imaging result, or GP letter — Kindra can then answer with your real labs, not just general health info.`}
        ctaLabel="Pick a file"
        onClick={() => navigate('/documents')}
        testid="todays-one-thing-upload-warm"
      />
    );
  }

  // 4. Continue your last chat? (only if last activity in last 24h)
  if (lastChat && lastChat.id && lastChat.last_activity) {
    const ageH = (Date.now() - new Date(lastChat.last_activity).getTime()) / 3600000;
    if (ageH <= 24) {
      const title = lastChat.title || 'your last conversation';
      return (
        <Card
          accent="cream"
          eyebrow="Today's one thing"
          headline={<>Continue <em className="italic">"{title}".</em></>}
          sub={`Pick up where you left off — Kindra still has the context.`}
          ctaLabel="Continue"
          onClick={() => navigate(`/chat?session=${lastChat.id}`)}
          testid="todays-one-thing-chat"
        />
      );
    }
  }

  // 5. Fallback — ask anything
  return (
    <Card
      accent="cream"
      eyebrow="Today's one thing"
      headline={<>Ask Kindra <em className="italic">anything.</em></>}
      sub={`A symptom, a result, a question for your next appointment. Kindra answers using your records when relevant.`}
      ctaLabel="Open chat"
      onClick={() => navigate('/chat')}
      testid="todays-one-thing-ask"
    />
  );
};

export default TodaysOneThing;
